import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PageLayout from '../components/PageLayout';
import Modal from '../components/Modal';
import client from '../api/client';
import { abandonAssessment } from '../api/assessment';
import ProfileNudgeModal from '../components/ProfileNudgeModal';
import useProfileGuard from '../hooks/useProfileGuard';
import Alert from '../components/Alert';
import CompetencyModelDiagram from '../components/CompetencyModelDiagram';
import './HomePage.css';

// 八项核心能力（三层八力）—— 文案与 wireframes-v2.html 对齐
const ABILITIES = [
  {
    no: 'Nº 01', layer: '认知准备层', name: '认知理解力',
    oneLine: '理解 AI 的基本能力、应用边界和主要局限，避免盲目使用或错误期待。',
    points: [
      '理解 AI 能做什么、不能做什么',
      '识别 AI 幻觉、事实错误和逻辑漏洞',
      '区分大模型、搜索工具、知识库和智能体',
    ],
  },
  {
    no: 'Nº 02', layer: '认知准备层', name: '学习适应力',
    oneLine: '面对 AI 技术变化，保持开放态度，主动尝试并持续调整自己的工作方式。',
    points: [
      '主动接触新的 AI 工具和方法',
      '面对输出不理想时持续追问和迭代',
      '将 AI 使用经验沉淀为方法、模板或案例',
    ],
  },
  {
    no: 'Nº 03', layer: '应用执行层', name: '场景判断力',
    oneLine: '根据任务目标、数据类型和风险要求，判断是否使用 AI，以及如何使用 AI。',
    points: [
      '判断哪些任务适合使用 AI',
      '选择合适的工具、模型或应用方式',
      '识别高风险场景和使用限制',
    ],
  },
  {
    no: 'Nº 04', layer: '应用执行层', name: '指令交互力',
    oneLine: '通过清晰、结构化、可迭代的表达，与 AI 进行高质量协同。',
    points: [
      '清楚表达背景、任务、目标和约束',
      '提供示例、标准、参考材料或输出格式',
      '通过多轮对话持续修正 AI 输出',
    ],
  },
  {
    no: 'Nº 05', layer: '应用执行层', name: '任务编排力',
    oneLine: '将复杂任务拆解为人机协同流程，让 AI 稳定参与真实工作过程。',
    points: [
      '拆解复杂任务的关键步骤',
      '判断每一步中人和 AI 的分工',
      '设计可复用的流程、模板和操作方法',
    ],
  },
  {
    no: 'Nº 06', layer: '价值产出层', name: '结果评估力',
    oneLine: '对 AI 输出进行事实核查、逻辑审查、质量判断和修正优化。',
    points: [
      '识别事实错误、虚假引用和编造信息',
      '判断内容是否符合任务目标和业务语境',
      '对 AI 结果进行核查、删改和再判断',
    ],
  },
  {
    no: 'Nº 07', layer: '价值产出层', name: '安全责任力',
    oneLine: '在数据安全、隐私保护、版权合规和责任边界内负责任地使用 AI。',
    points: [
      '识别敏感数据和隐私信息',
      '具备脱敏、最小必要输入和权限边界意识',
      '判断版权、合规、偏见和责任风险',
    ],
  },
  {
    no: 'Nº 08', layer: '价值产出层', name: '创新转化力',
    oneLine: '利用 AI 创造新成果、新方法和新场景，并推动个人或团队复用。',
    points: [
      '用 AI 生成方案、创意和行动路径',
      '将内容转化为报告、PPT、话术、流程等成果',
      '沉淀提示词、模板、流程和智能体方案',
    ],
  },
];

const VALUE_CARDS = [
  {
    biz: false,
    title: '对个人：看清自己的 AI 应用能力',
    vsub: '知道自己会什么、弱在哪、下一步该怎么提升。',
    points: [
      ['建立能力画像', '从三层八力出发，看清 AI 能力结构'],
      ['识别优势短板', '明确强项、薄弱项和使用风险'],
      ['判断当前阶段', '了解自己处于哪一级能力水平'],
      ['获得提升建议', '结合岗位场景，找到下一步训练方向'],
    ],
  },
  {
    biz: true,
    title: '对企业：支撑 AI 人才盘点与培养',
    vsub: '看清谁会用 AI、谁能用好 AI、谁适合先用 AI。',
    points: [
      ['统一能力标准', '用三层八力定义“会用 AI”的具体表现'],
      ['开展人才盘点', '识别 AI 应用骨干、潜力人才和培养对象'],
      ['指导培训设计', '根据团队短板设计分层课程和训练任务'],
      ['辅助人岗匹配', '判断不同岗位所需的 AI 应用能力基础'],
      ['识别使用风险', '发现盲信 AI、数据安全和合规边界问题'],
      ['沉淀组织经验', '形成提示词、模板、流程和应用案例'],
    ],
  },
];

function AbilityCard({ ability }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div
      className={`flip-card a-card${flipped ? ' flipped' : ''}`}
      onClick={() => setFlipped((v) => !v)}
    >
      <div className="flip-inner">
        <div className="flip-face front">
          <span className="a-num">{ability.layer} · {ability.no.replace('Nº ', '')}</span>
          <h4>{ability.name}</h4>
          <p className="one-line">{ability.oneLine}</p>
          <span className="flip-hint">点击查看详情 →</span>
        </div>
        <div className="flip-face back">
          <span className="layer-lid">{ability.layer} · {ability.no.replace('Nº ', '')}</span>
          <h4>{ability.name}</h4>
          <ul className="points">
            {ability.points.map((p) => <li key={p}>{p}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ValueCard({ card }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div
      className={`flip-card v-card${card.biz ? ' biz' : ''}${flipped ? ' flipped' : ''}`}
      onClick={() => setFlipped((v) => !v)}
    >
      <div className="flip-inner">
        <div className="flip-face front">
          <h4>{card.title}</h4>
          <p className="vsub">{card.vsub}</p>
          <span className="flip-hint">点击展开详细价值 →</span>
        </div>
        <div className="flip-face back">
          <button
            className="flip-close"
            aria-label="返回"
            onClick={(e) => { e.stopPropagation(); setFlipped(false); }}
          >×</button>
          <h4>{card.title}</h4>
          <ul>
            {card.points.map(([term, desc]) => (
              <li key={term}><b>{term}</b>：{desc}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // dialog: null | 'resume' | 'discard-confirm'
  const [dialog, setDialog] = useState(null);
  const [unfinished, setUnfinished] = useState(null);
  const [busy, setBusy] = useState(false);          // abandon → start 流程的禁用态
  const [starting, setStarting] = useState(false);  // CTA 点击后 profile/resume 并发查询
  const [dialogErr, setDialogErr] = useState('');
  const [fetchErr, setFetchErr] = useState('');
  const { guardOpen, checkAndGuard, closeGuard } = useProfileGuard();

  const handleStart = async () => {
    // 防双击：查询期间再次点击直接忽略
    if (starting) return;

    // ① 未登录 → 直跳注册（产品决定 A）
    if (!user) { navigate('/register'); return; }

    // ② 已登录 → 整个流程包外层 try/finally,确保 guard 等待期间按钮也 disabled
    setStarting(true); setFetchErr('');
    try {
      let resume = null;
      try {
        const { data } = await client.get('/assessments/resume/');
        resume = data;
      } catch (err) {
        // 401：让 axios 拦截器去处理 token 刷新 / 跳 /login，本组件不要再 navigate
        // 抢路由（拦截器在 refresh 失败时会执行 window.location.href = '/login'）。
        // 其它错误（5xx 等）才跳规则页让那边的现有兜底接手。
        if (err?.response?.status === 401) return;
        navigate('/assessment/rules');
        return;
      }

      // 有未完成测评 → Modal 让用户选「继续 / 开新」
      // 注：dialog 里点 [继续上次] 直接 navigate,不被 guard 拦
      //     dialog 里点 [开始新测评 → 丢弃旧] 走 handleDiscardOldAndStartNew,那里再 guard
      if (resume?.has_unfinished && resume.assessment) {
        setUnfinished(resume.assessment);
        setDialog('resume');
        return;
      }

      // 准备开新测评 → guard
      const status = await checkAndGuard();
      if (status === 'blocked_incomplete') return;          // hard modal 已弹
      if (status === 'blocked_auth') return;                // 让 axios 拦截器跳 /login
      if (status === 'blocked_error') {
        setFetchErr('暂时无法读取画像，请稍后重试');
        return;
      }
      // pass → 走深度选择页
      navigate('/assessment/rules');
    } finally {
      setStarting(false);   // 所有分支退出时统一清 starting
    }
  };

  // 彻底关闭弹窗链（× / 遮罩 / ESC）：把上下文清空回到首页。
  // discard-confirm 的「取消」按钮另走 backToResume，回到上一层而不是关掉整条。
  const closeDialog = () => {
    if (busy) return;
    setDialog(null);
    setDialogErr('');
  };

  const backToResume = () => {
    if (busy) return;
    setDialogErr('');
    setDialog('resume');
  };

  const handleDiscardOldAndStartNew = async () => {
    if (!unfinished?.id) return;
    setBusy(true); setDialogErr('');
    try {
      // guard 必须早于 abandon —— 防止用户被 gate 拦下时旧测评已经 abandon
      const status = await checkAndGuard();
      if (status === 'blocked_incomplete') {
        setDialog(null);          // 关 discard-confirm,避免与 hard gate 同 z-index (9999) 层叠
        return;                    // hard gate modal 自己弹出
      }
      if (status === 'blocked_auth') return;   // 让 axios 拦截器跳 /login
      if (status === 'blocked_error') {
        setDialogErr('暂时无法读取画像，请稍后重试');   // 错误显示在 discard-confirm 内(用户视线焦点)
        return;
      }

      // pass → 执行 abandon
      try {
        await abandonAssessment(unfinished.id);
      } catch (err) {
        // 后端 abandon 已是幂等：abandoned 再调返回 200。
        // 只有真正的失败（网络断、500 等）才落到这条分支。
        setDialogErr(err.response?.data?.detail || '操作失败，请稍后再试');
        return;
      }
      setDialog(null);
      setUnfinished(null);
      navigate('/assessment/rules');
    } finally {
      setBusy(false);   // 所有分支退出都清 busy,避免按钮卡 "处理中"
    }
  };

  // 第二个 CTA 按登录态分文案（产品决定）
  const secondaryCtaText = user ? '继续测评' : '立即注册开始测评';

  return (
    <PageLayout>
      <div className="v5-home">
        <ProfileNudgeModal />
        <ProfileNudgeModal mode="hard" open={guardOpen} onClose={closeGuard} />
        {/* ===== HERO ===== */}
        <section className="v5-hero">
          <h1>AIQ</h1>
          <h2>前哨 AI 胜任力测评系统</h2>
          <p className="v5-hero-sub">
            基于<b>前哨 AI 胜任力模型</b>，评估个人理解 AI、调动 AI、协同 AI、判断 AI、约束 AI，<br />
            并将 AI 转化为<b>稳定、可靠、可复用工作成果</b>的能力。
          </p>

          <div className="v5-hero-fig">
            <CompetencyModelDiagram />
          </div>

          {fetchErr && (
            <div style={{ marginBottom: 12, maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' }}>
              <Alert type="error">{fetchErr}</Alert>
            </div>
          )}
          <button
            className="v5-btn primary"
            onClick={handleStart}
            disabled={starting}
          >
            {starting ? '处理中…' : '开始测评 →'}
          </button>
        </section>

        {/* ===== 八项核心能力 ===== */}
        <section className="v5-eight">
          <div className="v5-head">
            <h2 className="v5-section-title">八项核心能力</h2>
            <p className="v5-section-sub">本次测评将围绕这 8 个维度展开，点击卡片查看详情。</p>
          </div>
          <div className="v5-grid">
            {ABILITIES.map((a) => <AbilityCard key={a.name} ability={a} />)}
          </div>
        </section>

        {/* ===== 价值双卡 ===== */}
        <section className="v5-value">
          <div className="v5-head">
            <h2 className="v5-section-title">看清个人 · 盘点团队</h2>
            <p className="v5-section-sub">对个人看清自己，对企业看清团队 —— 点击卡片展开详细价值。</p>
          </div>
          <div className="v5-value-row">
            {VALUE_CARDS.map((c) => <ValueCard key={c.title} card={c} />)}
          </div>
          <div className="v5-value-bar">
            基于<b>前哨 AI 胜任力模型</b>，将 AI 能力拆解为
            <b>可理解 · 可评估 · 可培养 · 可转化</b>的能力结构。
          </div>
        </section>

        {/* ===== 底部 CTA ===== */}
        <section className="v5-cta">
          <h2>准备好了吗？</h2>
          <p>测评结果将生成专属 AIQ 报告 · 支持下载</p>
          <button
            className="v5-btn primary"
            onClick={handleStart}
            disabled={starting}
          >
            {starting ? '处理中…' : `${secondaryCtaText} →`}
          </button>
        </section>

        {/* —— 弹窗:有未完成测评,选继续 / 开新 —— */}
        <Modal
          open={dialog === 'resume'}
          onClose={closeDialog}
          title="你有一次未完成的测评"
          footer={
            <>
              <button onClick={closeDialog} style={btnGhost}>关闭</button>
              <button
                onClick={() => setDialog('discard-confirm')}
                style={btnGhostDanger}
              >
                开始新测评
              </button>
              <button
                onClick={() => {
                  if (!unfinished?.id) return;
                  setDialog(null);
                  navigate(`/assessment/${unfinished.id}`);
                }}
                style={btnPrimary}
              >
                继续上次
              </button>
            </>
          }
        >
          <p style={modalP}>
            你之前还有一次测评没有完成。
            可以从上次的进度继续作答，或开始一次全新的测评。
          </p>
        </Modal>

        {/* —— 弹窗：开新测评 二次确认（点 × / 遮罩 / ESC 整链关闭；点「取消」回上一层） —— */}
        <Modal
          open={dialog === 'discard-confirm'}
          onClose={closeDialog}
          title="确认开始新测评？"
          closeOnBackdrop={!busy}
          closeOnEsc={!busy}
          footer={
            <>
              <button onClick={backToResume} disabled={busy} style={btnGhost}>取消</button>
              <button
                onClick={handleDiscardOldAndStartNew}
                disabled={busy}
                style={busy ? btnPrimaryDisabled : btnDanger}
              >
                {busy ? '处理中...' : '丢弃并开始新测评'}
              </button>
            </>
          }
        >
          <p style={modalP}>
            开始新测评后，上次未完成测评的进度<strong style={{color:'#dc2626'}}>将被丢弃</strong>且无法恢复。
            确认要继续吗？
          </p>
          {dialogErr && (
            <div style={{marginTop:12,padding:'8px 10px',background:'#fef2f2',color:'#dc2626',fontSize:13,borderRadius:4}}>
              {dialogErr}
            </div>
          )}
        </Modal>
      </div>
    </PageLayout>
  );
}

const btnBase = {
  padding: '10px 18px', border: 'none', borderRadius: 6,
  fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const btnGhost = { ...btnBase, background: '#f3f4f6', color: '#475569' };
const btnGhostDanger = { ...btnBase, background: 'transparent', color: '#dc2626', border: '1px solid #fecaca' };
const btnPrimary = { ...btnBase, background: '#2563eb', color: '#fff' };
const btnPrimaryDisabled = { ...btnPrimary, background: '#cbd5e1', cursor: 'not-allowed' };
const btnDanger = { ...btnBase, background: '#dc2626', color: '#fff' };
const modalP = { color: '#475569', fontSize: 14, margin: '0 0 4px', lineHeight: 1.7 };
